import { TextInput, useAnchoredDropdownStyle, useModalPortalTarget } from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ChangeEvent } from 'react';

import { useAmelioTranslations } from '@/hooks';

const DEFAULT_CUSTOM_COLOR = '#14B8A6';
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const HEX_3_REGEX = /^#[0-9a-fA-F]{3}$/;

export const LEVEL_COLOR_PRESETS: string[] = [
    // Row 1 — bright tints
    '#14B8A6',
    '#0EA5E9',
    '#3B82F6',
    '#6366F1',
    '#8B5CF6',
    '#A855F7',
    '#EC4899',
    '#EF4444',
    // Row 2 — warm and green tints
    '#F97316',
    '#F59E0B',
    '#EAB308',
    '#84CC16',
    '#22C55E',
    '#10B981',
    '#06B6D4',
    '#0284C7',
    // Row 3 — pastel and neutral tints
    '#99F6E4',
    '#BFDBFE',
    '#C7D2FE',
    '#DDD6FE',
    '#FBCFE8',
    '#FECACA',
    '#94A3B8',
    '#1E293B',
];

export interface LevelColorPickerProps {
    value: string | null;
    onChange: (color: string) => void;
    className?: string;
}

const normalizeHex = (hex: string): string => {
    const match = HEX_3_REGEX.test(hex)
        ? /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(hex)
        : null;

    if (match) {
        const [, r, g, b] = match;
        return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }

    return hex.toLowerCase();
};

const colorInputValue = (value: string | null): string =>
    value && HEX_COLOR_REGEX.test(value) ? normalizeHex(value) : DEFAULT_CUSTOM_COLOR;

export default function LevelColorPicker({ value, onChange, className }: LevelColorPickerProps) {
    const { t } = useAmelioTranslations();
    const [open, setOpen] = useState(false);
    const [hexText, setHexText] = useState(value ?? '');
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const portalTarget = useModalPortalTarget();

    const anchoredStyle = useAnchoredDropdownStyle({
        anchorRef: triggerRef,
        isOpen: open,
        placement: 'bottom-left',
    });
    const dropdownStyle = { ...anchoredStyle, width: '14rem' };

    useEffect(() => {
        if (open) {
            setHexText(value ?? '');
        }
    }, [open, value]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const path = event.composedPath();
            const insideTrigger = triggerRef.current && path.includes(triggerRef.current);
            const insideMenu = dropdownRef.current && path.includes(dropdownRef.current);

            if (!insideTrigger && !insideMenu) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [open]);

    const selectedValue = value;
    const rootClassName = className ? `relative shrink-0 ${className}` : 'relative shrink-0';

    const handlePresetClick = (preset: string) => {
        onChange(preset);
        setOpen(false);
    };

    const handleColorInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const color = event.target.value;
        onChange(color);
        setHexText(color);
    };

    const handleHexTextChange = (raw: string) => {
        const trimmed = raw.trim();
        setHexText(raw);

        if (HEX_COLOR_REGEX.test(trimmed)) {
            onChange(normalizeHex(trimmed));
        }
    };

    return (
        <div
            ref={triggerRef}
            className={rootClassName}
            data-testid={'level-color-picker'}
        >
            <button
                type={'button'}
                onClick={() => setOpen((current) => !current)}
                aria-label={t('competencies.levels.iconColor')}
                title={t('competencies.levels.iconColor')}
                className={
                    'border-base-200 hover:border-primary flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border bg-white/80 transition-colors'
                }
                data-testid={'level-color-picker-trigger'}
            >
                <span
                    className={'h-5 w-5 rounded-full border border-black/10'}
                    style={
                        selectedValue
                            ? { background: selectedValue }
                            : {
                                  backgroundImage:
                                      'conic-gradient(#EF4444,#F59E0B,#22C55E,#14B8A6,#3B82F6,#8B5CF6,#EC4899,#EF4444)',
                              }
                    }
                />
            </button>

            {open &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        style={dropdownStyle}
                        className={'rounded-lg border border-gray-200 bg-white p-3 shadow-lg'}
                    >
                        <div className={'grid grid-cols-8 gap-1.5'}>
                            {LEVEL_COLOR_PRESETS.map((preset) => {
                                const isSelected =
                                    selectedValue?.toLowerCase() === preset.toLowerCase();

                                return (
                                    <button
                                        key={preset}
                                        type={'button'}
                                        onClick={() => handlePresetClick(preset)}
                                        aria-label={preset}
                                        title={preset}
                                        className={`h-5 w-5 rounded-full border transition-transform hover:scale-110 ${
                                            isSelected
                                                ? 'border-navy ring-navy/30 ring-2'
                                                : 'border-black/10'
                                        }`}
                                        style={{ background: preset }}
                                    />
                                );
                            })}
                        </div>

                        <div className={'my-2 h-px bg-gray-100'} />

                        <div className={'flex items-center gap-2'}>
                            <input
                                type={'color'}
                                value={colorInputValue(selectedValue)}
                                onChange={handleColorInputChange}
                                aria-label={t('competencies.levels.customColor')}
                                title={t('competencies.levels.customColor')}
                                className={
                                    'border-base-200 h-8 w-10 cursor-pointer rounded border bg-white p-1'
                                }
                                data-testid={'level-color-picker-color-input'}
                            />

                            <TextInput
                                value={hexText}
                                onChange={handleHexTextChange}
                                placeholder={DEFAULT_CUSTOM_COLOR}
                                size={'sm'}
                                textSize={'sm'}
                                variant={'white'}
                                bordered={true}
                                ariaLabel={t('competencies.levels.customColor')}
                                dataTestId={'level-color-picker-hex-input'}
                                className={'flex-1'}
                            />
                        </div>
                    </div>,
                    portalTarget || document.body,
                )}
        </div>
    );
}
